import { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { StarIcon, CheckCircleIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sf * { box-sizing:border-box; }
  .sf { font-family:'Sora',sans-serif; color:#1a1d2e; animation:sfFade .4s ease both; }
  @keyframes sfFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .sf-hdr { margin-bottom:1.5rem; }
  .sf-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .sf-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .progress-banner { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:14px 18px; margin-bottom:1.2rem; display:flex; align-items:center; gap:14px; flex-wrap:wrap; }
  .pb-ring { position:relative; width:52px; height:52px; flex-shrink:0; }
  .pb-ring-text { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; font-size:.72rem; font-weight:700; font-family:'JetBrains Mono',monospace; color:#8b5cf6; }
  .pb-info { flex:1; }
  .pb-title { font-size:.86rem; font-weight:700; color:#0f1123; }
  .pb-sub   { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .sf-list { display:flex; flex-direction:column; gap:8px; }
  .sf-item { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:14px 18px; display:flex; align-items:center; gap:12px; cursor:pointer; transition:all .15s; }
  .sf-item:hover { border-color:#8b5cf6; box-shadow:0 4px 14px rgba(139,92,246,.1); transform:translateY(-1px); }
  .sf-item.submitted { opacity:.7; cursor:default; }
  .sf-item.submitted:hover { transform:none; box-shadow:none; border-color:#e8ebf4; }
  .sf-icon { width:38px; height:38px; border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0; font-size:1.1rem; }
  .sf-icon.subject { background:#eef2ff; }
  .sf-icon.class_teacher { background:#f0fdfa; }
  .sf-item-info { flex:1; min-width:0; }
  .sf-item-title { font-size:.84rem; font-weight:700; color:#0f1123; }
  .sf-item-sub   { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .sf-item-deadline { font-size:.68rem; color:#f59e0b; font-weight:600; margin-top:2px; }
  .sf-badge { display:inline-flex; font-size:.67rem; font-weight:700; padding:3px 9px; border-radius:99px; white-space:nowrap; flex-shrink:0; }
  .sf-badge.pending   { background:#f3f0ff; color:#8b5cf6; }
  .sf-badge.submitted { background:#ecfdf5; color:#10c98e; }
  /* Form view */
  .form-view { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .fv-head { padding:18px 22px; border-bottom:1px solid #f0f2f8; }
  .fv-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .fv-meta  { font-size:.74rem; color:#9094a8; margin-top:4px; }
  .fv-body  { padding:22px; }
  .question-block { margin-bottom:22px; padding-bottom:22px; border-bottom:1px solid #f3f5fb; }
  .question-block:last-child { border-bottom:none; margin-bottom:0; padding-bottom:0; }
  .q-number { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-bottom:5px; }
  .q-text { font-size:.88rem; font-weight:600; color:#0f1123; margin-bottom:10px; line-height:1.5; }
  .q-required { color:#ef4444; margin-left:3px; }
  /* Rating stars */
  .star-rating { display:flex; gap:6px; align-items:center; flex-wrap:wrap; }
  .star-btn { width:40px; height:40px; border-radius:10px; border:2px solid #e0e4f0; background:white; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:.15s; font-size:1.2rem; }
  .star-btn.selected { border-color:#f59e0b; background:#fffbeb; }
  .star-btn:hover { border-color:#f59e0b; }
  .star-label { font-size:.72rem; color:#9094a8; margin-left:4px; }
  /* Yes/No */
  .yn-row { display:flex; gap:8px; }
  .yn-btn { flex:1; padding:10px; border-radius:10px; border:1.5px solid #e0e4f0; background:white; cursor:pointer; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; transition:.15s; }
  .yn-btn.yes.selected { border-color:#10c98e; background:#ecfdf5; color:#10c98e; }
  .yn-btn.no.selected  { border-color:#ef4444; background:#fef2f2; color:#ef4444; }
  .yn-btn:hover { border-color:#8b5cf6; }
  /* Text */
  .q-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:10px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:#f8f9fc; width:100%; resize:vertical; min-height:80px; outline:none; }
  .q-textarea:focus { border-color:#8b5cf6; background:white; }
  .fv-foot { padding:16px 22px; border-top:1px solid #f0f2f8; display:flex; gap:8px; justify-content:flex-end; flex-wrap:wrap; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-purple { background:#8b5cf6; color:white; } .btn-purple:hover { background:#7c3aed; }
  .btn-ghost  { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .sf-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; background:white; border-radius:16px; border:1px solid #e8ebf4; }
  .sf-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#8b5cf6; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg)} }
  .anon-notice { background:#f0f4ff; border:1px solid #c7d2fe; border-radius:10px; padding:10px 14px; font-size:.76rem; color:#3730a3; margin-bottom:16px; }
  @media (max-width:600px) { .sf-title{font-size:1.1rem;} .fv-body{padding:16px;} .fv-foot{justify-content:stretch;} .fv-foot .btn{flex:1;justify-content:center;} .star-btn{width:36px;height:36px;} }
`;

const STAR_LABELS = {1:'Poor',2:'Fair',3:'Good',4:'Very Good',5:'Excellent'};

const SmallRing = ({ pct }) => {
  const r=20, stroke=5, circ=2*Math.PI*r, dash=(pct/100)*circ;
  return (
    <svg width="52" height="52" viewBox="0 0 52 52" style={{transform:'rotate(-90deg)'}}>
      <circle cx="26" cy="26" r={r} fill="none" stroke="#f0f2f8" strokeWidth={stroke}/>
      <circle cx="26" cy="26" r={r} fill="none" stroke="#8b5cf6" strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"/>
    </svg>
  );
};

export default function StudentFeedback() {
  const [list,     setList]    = useState([]);
  const [loading,  setLoading] = useState(true);
  const [active,   setActive]  = useState(null); // current form being filled
  const [formData, setFormData]= useState(null);
  const [answers,  setAnswers] = useState({});
  const [submitting,setSubmitting]=useState(false);

  useEffect(() => { loadList(); }, []);

  const loadList = async () => {
    setLoading(true);
    try { const { data } = await api.get('/student/feedback'); setList(data); }
    catch { toast.error('Failed to load feedback forms'); }
    finally { setLoading(false); }
  };

  const openForm = async (item) => {
    if (item.isSubmitted) return;
    try {
      const { data } = await api.get(`/student/feedback/${item.responseId}`);
      setFormData(data);
      // Restore saved answers
      const saved = {};
      (data.savedAnswers||[]).forEach(a => { saved[a.questionId] = a; });
      setAnswers(saved);
      setActive(item);
    } catch (err) {
      if (err.response?.data?.isSubmitted) {
        toast('Already submitted');
        loadList();
      } else {
        toast.error(err.response?.data?.message || 'Failed to load form');
      }
    }
  };

  const setAnswer = (questionId, type, value) => {
    setAnswers(p => ({ ...p, [questionId]: { questionId, type, ...value } }));
  };

  const buildAnswers = () => Object.values(answers);

  const handleSaveDraft = async () => {
    try {
      await api.put(`/student/feedback/${active.responseId}/save`, { answers: buildAnswers() });
      toast.success('Draft saved');
    } catch { toast.error('Failed to save draft'); }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/student/feedback/${active.responseId}/submit`, { answers: buildAnswers() });
      toast.success('Feedback submitted! Thank you 🎉');
      setActive(null); setFormData(null); setAnswers({});
      loadList();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const pending   = list.filter(i => !i.isSubmitted);
  const submitted = list.filter(i =>  i.isSubmitted);
  const pct       = list.length > 0 ? Math.round((submitted.length/list.length)*100) : 0;

  const daysLeft = (endDate) => {
    const d = Math.ceil((new Date(endDate)-new Date())/86400000);
    return d > 0 ? `${d} day${d!==1?'s':''} left` : 'Ending today';
  };

  // ── FORM FILL VIEW ────────────────────────────────────────────────────────
  if (active && formData) return (
    <div className="sf"><style>{css}</style>
      <div className="sf-hdr">
        <div className="sf-title">{formData.evalType==='class_teacher'?'🏠 Class Teacher':'📚 Subject'} Evaluation</div>
        <div className="sf-sub">{formData.evalType==='class_teacher'?'Class Teacher':'Subject'}: <strong>{formData.teacher.name}</strong>{formData.subject?` · ${formData.subject}`:''} · {formData.className}</div>
      </div>
      <div className="form-view">
        <div className="fv-head">
          <div className="fv-title">{formData.formTitle}</div>
          <div className="fv-meta">{formData.questions?.length} questions</div>
        </div>
        <div className="fv-body">
          <div className="anon-notice">
            🔒 Your feedback is <strong>completely anonymous</strong>. Teachers can only see overall averages — never individual responses.
          </div>
          {(formData.questions||[]).map((q, i) => {
            const ans = answers[q._id];
            return (
              <div key={q._id} className="question-block">
                <div className="q-number">Question {i+1} of {formData.questions.length}</div>
                <div className="q-text">{q.text}{q.required&&<span className="q-required">*</span>}</div>
                {q.type === 'rating' && (
                  <div className="star-rating">
                    {[1,2,3,4,5].map(star => (
                      <button key={star} className={`star-btn${ans?.rating===star?' selected':''}`}
                        onClick={()=>setAnswer(q._id,'rating',{rating:star})}>
                        {star}⭐
                      </button>
                    ))}
                    {ans?.rating && <span className="star-label">{STAR_LABELS[ans.rating]}</span>}
                  </div>
                )}
                {q.type === 'yes_no' && (
                  <div className="yn-row">
                    <button className={`yn-btn yes${ans?.yesNo===true?' selected':''}`} onClick={()=>setAnswer(q._id,'yes_no',{yesNo:true})}>✓ Yes</button>
                    <button className={`yn-btn no${ans?.yesNo===false?' selected':''}`}  onClick={()=>setAnswer(q._id,'yes_no',{yesNo:false})}>✗ No</button>
                  </div>
                )}
                {q.type === 'text' && (
                  <textarea className="q-textarea" placeholder="Write your comments here…" value={ans?.text||''} onChange={e=>setAnswer(q._id,'text',{text:e.target.value})} />
                )}
              </div>
            );
          })}
        </div>
        <div className="fv-foot">
          <button className="btn btn-ghost" onClick={()=>{setActive(null);setFormData(null);}}>← Back</button>
          <button className="btn btn-ghost" onClick={handleSaveDraft}>Save Draft</button>
          <button className="btn btn-purple" onClick={handleSubmit} disabled={submitting}>
            <CheckCircleIcon />{submitting?'Submitting…':'Submit Feedback'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── LIST VIEW ─────────────────────────────────────────────────────────────
  return (
    <div className="sf"><style>{css}</style>
      <div className="sf-hdr">
        <div className="sf-title">Teacher Evaluation</div>
        <div className="sf-sub">Rate your teachers anonymously — your identity is never revealed</div>
      </div>

      {loading ? (
        <div className="sf-loading"><div className="spinner"/>Loading…</div>
      ) : list.length === 0 ? (
        <div className="sf-empty">
          <CheckCircleIcon style={{width:32,height:32,margin:'0 auto 12px',display:'block',opacity:.3}}/>
          No active feedback campaigns at the moment.
        </div>
      ) : (
        <>
          {/* Progress ring */}
          <div className="progress-banner">
            <div className="pb-ring">
              <SmallRing pct={pct}/>
              <div className="pb-ring-text">{pct}%</div>
            </div>
            <div className="pb-info">
              <div className="pb-title">{submitted.length} of {list.length} completed</div>
              <div className="pb-sub">{pending.length > 0 ? `${pending.length} form${pending.length!==1?'s':''} pending` : 'All done! Great job 🎉'}</div>
            </div>
          </div>

          {/* Pending */}
          {pending.length > 0 && (
            <>
              <div style={{fontSize:'.72rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Pending</div>
              <div className="sf-list" style={{marginBottom:16}}>
                {pending.map(item => (
                  <div key={item.responseId} className="sf-item" onClick={()=>openForm(item)}>
                    <div className={`sf-icon ${item.evalType}`}>{item.evalType==='class_teacher'?'🏠':'📚'}</div>
                    <div className="sf-item-info">
                      <div className="sf-item-title">
                        {item.evalType==='class_teacher' ? 'Class Teacher' : item.subject} — {item.teacher.name}
                      </div>
                      <div className="sf-item-sub">{item.className} · {item.formTitle}</div>
                      {item.endDate && <div className="sf-item-deadline">⏰ {daysLeft(item.endDate)}</div>}
                    </div>
                    <span className="sf-badge pending">Pending</span>
                    <ChevronRightIcon style={{width:16,height:16,stroke:'#9094a8',strokeWidth:2,flexShrink:0}}/>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Submitted */}
          {submitted.length > 0 && (
            <>
              <div style={{fontSize:'.72rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Submitted</div>
              <div className="sf-list">
                {submitted.map(item => (
                  <div key={item.responseId} className="sf-item submitted">
                    <div className={`sf-icon ${item.evalType}`}>{item.evalType==='class_teacher'?'🏠':'📚'}</div>
                    <div className="sf-item-info">
                      <div className="sf-item-title">{item.evalType==='class_teacher'?'Class Teacher':item.subject} — {item.teacher.name}</div>
                      <div className="sf-item-sub">{item.className} · {item.formTitle}</div>
                    </div>
                    <span className="sf-badge submitted">✓ Submitted</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}